"use client";

import React from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const partners = [{
  name: "Bruker",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/partner_bruker_logo.png",
  description: "High-performance scientific instruments and analytical solutions"
}, {
  name: "VIAVI Solutions",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/partner_viavi_logo.png",
  description: "Optical security and miniature spectroscopy instruments"
}, {
  name: "Rigaku",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/partner_rigaku_logo.png",
  description: "X-ray analytical and industrial instrumentation"
}, {
  name: "Copley Scientific",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/partner_copley_logo.png",
  description: "Inhaler testing equipment and pharmaceutical test solutions"
}, {
  name: "ReSolve",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/partner_resolve_logo.png",
  description: "Handheld Raman spectroscopy and chemical identification"
}];
export default function Partners() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="partnersanddemocta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container flex flex-col gap-16 md:gap-20">
        {/* Partner Logos Header & Grid */}
        <div className="flex flex-col items-center text-center gap-10">
          <div className="max-w-2xl flex flex-col items-center gap-3">
            <span className="text-xs font-semibold tracking-wider uppercase text-muted-foreground">
              Trusted Global Manufacturers
            </span>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground font-default">
              Our Technology Partners
            </h2>
            <p className="text-sm md:text-base text-muted-foreground font-default">
              We partner with industry-leading manufacturers to bring analytical instrumentation and technical support to regulated laboratories.
            </p>
          </div>

          {/* Logo Row */}
          <div className="w-full grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {partners.map((partner, i) => <motion.div key={partner.name} data-index={i} initial={{
            opacity: prefersReducedMotion ? 1 : 0,
            y: prefersReducedMotion ? 0 : 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: prefersReducedMotion ? 0 : i * 0.08
          }} className="h-full">
                <Link to="/SuppliersPage" className="group flex flex-col items-center justify-center p-6 h-full min-h-[120px] rounded-xl border border-border bg-card hover:border-primary/50 hover:shadow-md transition-[border-color,box-shadow,transform] hover:-translate-y-0.5" aria-label={`View ${partner.name} supplier details`}>
                  <div className="flex items-center justify-center h-10 w-full mb-2">
                    <img src={partner.logo} alt={`${partner.name} logo`} className="h-7 max-w-[130px] object-contain grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-200" loading="lazy" />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                    {partner.name}
                  </span>
                </Link>
              </motion.div>)}
          </div>
        </div>

        {/* Request a Demonstration CTA Block */}
        <motion.div initial={{
        opacity: prefersReducedMotion ? 1 : 0,
        y: prefersReducedMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="relative overflow-hidden rounded-2xl bg-sidebar text-sidebar-foreground border border-sidebar-border p-8 md:p-12 lg:p-16 shadow-xl">
          {/* Subtle Background Elements */}
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-sidebar-accent/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-primary/10 blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-2xl flex flex-col gap-3">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <Sparkles className="size-4" />
                <span>Hands-on Evaluation</span>
              </div>
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-sidebar-foreground font-default">
                See our technology in action.
              </h3>
              <p className="text-sm md:text-base text-sidebar-foreground/80 font-default">
                Experience equipment capability on your specific samples. Our application specialists arrange on-site or remote system demonstrations tailored to your laboratory workflow.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full lg:w-auto shrink-0">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-medium px-8 h-12 text-base">
                <Link to="/BookADemoPage">
                  Request a Demonstration
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}